'use client';

import React from 'react';
import {
  // LuInbox,
  LuSearch,
  LuShieldOff,
  LuCircleAlert,
  LuPackageOpen,
  LuFileQuestion,
  LuUpload,
  LuExternalLink,
  LuX,
} from 'react-icons/lu';
import type { IconType } from 'react-icons';

import { Box } from './box';
import { VStack, HStack } from './stack';
import { Text } from './text';
import { Button } from './button';

export type EmptyStateVariant = 'no-data' | 'no-results' | 'no-permissions' | 'error' | 'custom';

export interface EmptyStateProps {
  /**
   * The variant of the empty state
   */
  variant?: EmptyStateVariant;
  /**
   * Custom icon - can be either an IconType component (e.g., LuSearch)
   * or a rendered React element (e.g., <LuSearch />)
   */
  icon?: IconType | React.ReactElement;
  /**
   * Icon size in pixels
   */
  iconSize?: number;
  /**
   * Icon color
   */
  iconColor?: string;
  /**
   * Title text
   */
  title?: string;
  /**
   * Description text
   */
  description?: string;
  /**
   * Primary action button text
   */
  actionText?: string;
  /**
   * Callback when action button is clicked
   */
  onAction?: () => void;
  /**
   * Secondary action button text
   */
  secondaryActionText?: string;
  /**
   * Callback when secondary action button is clicked
   */
  onSecondaryAction?: () => void;
  /**
   * Custom illustration component (replaces icon)
   */
  illustration?: React.ReactNode;
  /**
   * Maximum width for the content
   */
  maxWidth?: string;
  /**
   * Spacing between elements
   */
  spacing?: number;
  /**
   * Search suggestions to display
   */
  searchSuggestions?: string[];
  /**
   * Callback when a search suggestion is clicked
   */
  onSearchSuggestion?: (suggestion: string) => void;
  /**
   * Import options to display
   */
  importOptions?: Array<{
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  }>;
  /**
   * Active filters to display
   */
  activeFilters?: Array<{
    label: string;
    value: string;
  }>;
  /**
   * Callback when clear filters is clicked
   */
  onClearFilters?: () => void;
  /**
   * Help links to display
   */
  helpLinks?: Array<{
    label: string;
    href: string;
    external?: boolean;
  }>;
  /**
   * Additional content to render below the main content
   */
  additionalContent?: React.ReactNode;
  /**
   * i18n translations
   */
  translations?: {
    noData?: {
      title?: string;
      description?: string;
      action?: string;
    };
    noResults?: {
      title?: string;
      description?: string;
      action?: string;
    };
    noPermissions?: {
      title?: string;
      description?: string;
      action?: string;
    };
    error?: {
      title?: string;
      description?: string;
      action?: string;
    };
    searchSuggestions?: string;
    importData?: string;
    clearFilters?: string;
    helpfulResources?: string;
  };
}

// Default icons for each variant
const variantIcons: Record<EmptyStateVariant, IconType> = {
  'no-data': LuPackageOpen,
  'no-results': LuSearch,
  'no-permissions': LuShieldOff,
  error: LuCircleAlert,
  custom: LuFileQuestion,
};

// Default translations
const defaultTranslations = {
  noData: {
    title: 'No data yet',
    description: 'Add a new record to get started',
    action: 'Add first record',
  },
  noResults: {
    title: 'No results found',
    description: 'Try different search criteria',
    action: 'Clear filters',
  },
  noPermissions: {
    title: 'Access denied',
    description: "You don't have permission to view this content",
    action: 'Request access',
  },
  error: {
    title: 'Something went wrong',
    description: 'An unexpected error occurred. Please try again',
    action: 'Try again',
  },
  searchSuggestions: 'Try searching for:',
  importData: 'Import data',
  clearFilters: 'Clear all filters',
  helpfulResources: 'Helpful resources',
};

export function EmptyState({
  variant = 'no-data',
  icon,
  iconSize = 48,
  iconColor = '#6C757D',
  title,
  description,
  actionText,
  onAction,
  secondaryActionText,
  onSecondaryAction,
  illustration,
  maxWidth = '400px',
  spacing = 4,
  searchSuggestions,
  onSearchSuggestion,
  importOptions,
  activeFilters,
  onClearFilters,
  helpLinks,
  additionalContent,
  translations = {},
}: EmptyStateProps) {
  // Merge translations with defaults
  const t = {
    noData: { ...defaultTranslations.noData, ...translations.noData },
    noResults: { ...defaultTranslations.noResults, ...translations.noResults },
    noPermissions: { ...defaultTranslations.noPermissions, ...translations.noPermissions },
    error: { ...defaultTranslations.error, ...translations.error },
    searchSuggestions: translations.searchSuggestions || defaultTranslations.searchSuggestions,
    importData: translations.importData || defaultTranslations.importData,
    clearFilters: translations.clearFilters || defaultTranslations.clearFilters,
    helpfulResources: translations.helpfulResources || defaultTranslations.helpfulResources,
  };

  // Get variant-specific content
  const getVariantContent = () => {
    switch (variant) {
      case 'no-data':
        return {
          title: title || t.noData.title,
          description: description || t.noData.description,
          actionText: actionText || t.noData.action,
        };
      case 'no-results':
        return {
          title: title || t.noResults.title,
          description: description || t.noResults.description,
          actionText: actionText || t.noResults.action,
        };
      case 'no-permissions':
        return {
          title: title || t.noPermissions.title,
          description: description || t.noPermissions.description,
          actionText: actionText || t.noPermissions.action,
        };
      case 'error':
        return {
          title: title || t.error.title,
          description: description || t.error.description,
          actionText: actionText || t.error.action,
        };
      default:
        return {
          title: title || 'No content',
          description: description || 'There is no content to display',
          actionText: actionText || 'Take action',
        };
    }
  };

  const content = getVariantContent();

  // Determine the icon to render
  const renderIcon = () => {
    // If icon is a React element (e.g., <LuSearch />), render it directly
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon as React.ReactElement<{ size?: number; color?: string }>, {
        size: iconSize,
        color: iconColor,
      });
    }
    // If icon is an IconType component (e.g., LuSearch), create element
    const IconComponent = icon || variantIcons[variant];
    return React.createElement(
      IconComponent as React.ComponentType<{ size: number; color: string }>,
      { size: iconSize, color: iconColor }
    );
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="400px"
      padding={8}
      width="100%"
    >
      <VStack gap={spacing} maxWidth={maxWidth} textAlign="center">
        {/* Icon or Illustration */}
        {illustration ? (
          <Box>{illustration}</Box>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={iconSize * 2}
            height={iconSize * 2}
            borderRadius="full"
            backgroundColor="#F8F9FA"
          >
            {renderIcon()}
          </Box>
        )}

        {/* Text Content */}
        <VStack gap={2}>
          <Text fontSize="lg" fontWeight="semibold" color="#212529">
            {content.title}
          </Text>
          <Text fontSize="sm" color="#6C757D" maxWidth="300px">
            {content.description}
          </Text>
        </VStack>

        {/* Actions */}
        {(onAction || onSecondaryAction) && (
          <VStack gap={2} paddingTop={2}>
            {onAction && (
              <Button colorPalette="blue" variant="primary" onClick={onAction} size="sm">
                {content.actionText}
              </Button>
            )}
            {onSecondaryAction && (
              <Button colorPalette="gray" variant="ghost" onClick={onSecondaryAction} size="sm">
                {secondaryActionText}
              </Button>
            )}
          </VStack>
        )}

        {/* Search Suggestions */}
        {searchSuggestions && searchSuggestions.length > 0 && (
          <VStack gap={2} paddingTop={4}>
            <Text fontSize="sm" color="#6C757D">
              {t.searchSuggestions}
            </Text>
            <HStack gap={2} flexWrap="wrap" justifyContent="center">
              {searchSuggestions.map((suggestion, index) => (
                <Button
                  key={index}
                  size="sm"
                  variant="outline"
                  colorPalette="gray"
                  onClick={() => onSearchSuggestion?.(suggestion)}
                >
                  <LuSearch size={14} />
                  {suggestion}
                </Button>
              ))}
            </HStack>
          </VStack>
        )}

        {/* Active Filters */}
        {activeFilters && activeFilters.length > 0 && (
          <VStack gap={2} paddingTop={4}>
            <Text fontSize="sm" color="#6C757D">
              Active filters:
            </Text>
            <HStack gap={2} flexWrap="wrap" justifyContent="center">
              {activeFilters.map((filter, index) => (
                <Box
                  key={index}
                  display="inline-flex"
                  alignItems="center"
                  gap={1}
                  padding="4px 12px"
                  backgroundColor="#F3F4F6"
                  borderRadius="full"
                  fontSize="sm"
                >
                  <Text fontWeight="medium">{filter.label}:</Text>
                  <Text>{filter.value}</Text>
                </Box>
              ))}
            </HStack>
            {onClearFilters && (
              <Button size="sm" variant="ghost" colorPalette="gray" onClick={onClearFilters}>
                <LuX size={14} />
                {t.clearFilters}
              </Button>
            )}
          </VStack>
        )}

        {/* Import Options */}
        {importOptions && importOptions.length > 0 && (
          <VStack gap={2} paddingTop={4}>
            <Text fontSize="sm" color="#6C757D">
              {t.importData}:
            </Text>
            <HStack gap={2} flexWrap="wrap" justifyContent="center">
              {importOptions.map((option, index) => (
                <Button
                  key={index}
                  size="sm"
                  variant="outline"
                  colorPalette="gray"
                  onClick={option.onClick}
                >
                  {option.icon || <LuUpload size={14} />}
                  {option.label}
                </Button>
              ))}
            </HStack>
          </VStack>
        )}

        {/* Help Links */}
        {helpLinks && helpLinks.length > 0 && (
          <VStack gap={2} paddingTop={4}>
            <Text fontSize="sm" color="#6C757D">
              {t.helpfulResources}:
            </Text>
            <HStack gap={3} flexWrap="wrap" justifyContent="center">
              {helpLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#0066CC',
                    fontSize: '14px',
                    textDecoration: 'underline',
                  }}
                >
                  {link.label}
                  {link.external && <LuExternalLink size={12} />}
                </a>
              ))}
            </HStack>
          </VStack>
        )}

        {/* Additional Content */}
        {additionalContent && <Box paddingTop={4}>{additionalContent}</Box>}
      </VStack>
    </Box>
  );
}

// Export with display name for debugging
EmptyState.displayName = 'EmptyState';
